import React from 'react';
import useSWR from 'swr';
import './earnings-list.css'; // Specific CSS for earnings list
import EarningItem from './EarningItem';
import ReferralLink from './ReferralLink';
import { LOYALTY_API_URL } from 'configuration/urls';
import { LOYALTY_DEFAULTS } from './constants';

// Helper function to get earnings URL
const getEarningsUrl = (settingId, level = LOYALTY_DEFAULTS.LEVEL) => {
  if (!settingId) return null;
  return `${LOYALTY_API_URL}loyaltyEarnings?settingId=${settingId}&level=${level}`;
};

// SWR fetcher function for earnings
const fetchEarningsSWR = async (url) => {
  if (!url) return null;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch earnings: ${response.status}`);
    }
    const data = await response.json();
    return data?.data || [];
  } catch (error) {
    throw error;
  }
};

// Main EarningsList Component
const EarningsList = ({
  settingId,
  level,
  themeColors,
  pointsTerminology = LOYALTY_DEFAULTS.POINTS_TERMINOLOGY,
  userLoyaltyDetails,
  storeConfig,
}) => {
  const earningsKey = getEarningsUrl(settingId, level);

  const {
    data: earnings,
    error,
    isLoading,
  } = useSWR(earningsKey, fetchEarningsSWR, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 10000, // dedupe requests within 10 seconds
  });

  // Check if there's a referral earning rule (type: 2)
  const referralRule = earnings?.find(earning => earning.type === 2 && earning.isActive);

  if (isLoading) {
    return (
      <div className='earnings-list-loading'>
        {[1, 2, 3].map((item) => (
          <div key={item} className='earning-placeholder'></div>
        ))}
      </div>
    );
  }

  if (error) {
    return <div className='earnings-error'>Failed to load earnings</div>;
  }

  return (
    <div className='earnings-list-container'>
      {/* Referral Link Component - only show if referral rule exists */}
      {referralRule && (
        <ReferralLink
          userLoyaltyDetails={userLoyaltyDetails}
          themeColors={themeColors}
          pointsTerminology={pointsTerminology}
          referralRule={referralRule}
        />
      )}

      {/* Regular Earnings - filter out referral rules (type: 2 (referral)) */}
      {earnings && earnings.length > 0 ? (
        earnings
          .filter(earning => earning.type !== 2 && earning.isActive) // Exclude referral rules and inactive earnings
          .map((earning, index, filteredArray) => (
            <React.Fragment key={earning.id || index}>
              <EarningItem
                earning={earning}
                themeColors={themeColors}
                pointsTerminology={pointsTerminology}
                currency={storeConfig?.currency || LOYALTY_DEFAULTS.CURRENCY_SYMBOL}
              />
              {index < filteredArray.length - 1 && (
                <div className='earnings-divider'></div>
              )}
            </React.Fragment>
          ))
      ) : referralRule ? (
        // If we only have referral rules and no other earnings, don't show "no earnings" message
        null
      ) : (
        <div className='no-earnings'>
          No earning opportunities available at the moment
        </div>
      )}
    </div>
  );
};

export default EarningsList;
